import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import api from '../lib/api';
import { WorkspaceThumbnail } from '../components/WorkspaceThumbnail';
import { InviteDto } from '@prometeus/contracts';
import { useEventBusStore } from '../stores/event-bus.store';

export const Route = createFileRoute('/invites')({
  component: InvitesPage,
});

function InvitesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addToast = useEventBusStore((state) => state.addToast);
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'rejected'>('all');

  const { data: invites, isLoading } = useQuery<InviteDto[]>({
    queryKey: ['myInvites'],
    queryFn: async () => {
      const response = await api.get('/invites/my');
      return response.data.data;
    },
  });

  const acceptMutation = useMutation({
    mutationFn: async ({ token }: { token: string; workspaceId: string }) => {
      const response = await api.post(`/invites/${token}/accept`);
      return response.data.data;
    },
    onSuccess: (_data, { workspaceId }) => {
      queryClient.invalidateQueries({ queryKey: ['myInvites'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      addToast('Convite aceito com sucesso!', 'success');
      navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
    },
    onError: (error: any) => {
      addToast(error.response?.data?.message || 'Erro ao aceitar convite', 'error');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (token: string) => {
      const response = await api.post(`/invites/${token}/reject`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myInvites'] });
      addToast('Convite rejeitado', 'success');
    },
    onError: (error: any) => {
      addToast(error.response?.data?.message || 'Erro ao rejeitar convite', 'error');
    },
  });

  const filteredInvites = invites?.filter((invite) => {
    if (filter === 'all') return true;
    return invite.status === filter;
  }) || [];

  const pendingCount = invites?.filter((invite) => invite.status === 'pending').length || 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div
            className="bg-white dark:bg-gray-800 flex flex-col flex-1"
            style={{
              borderRadius: 15,
              boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div className="flex flex-col gap-4 flex-shrink-0 p-5 pb-4" style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}>
              <h1
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#06141B',
                }}
              >
                Meus convites
              </h1>
            </div>
            <div className="flex-1 flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#485A6A' }} />
              <span className="ml-3" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#718096' }}>
                Carregando convites...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-8 py-6">
        <div
          className="bg-white dark:bg-gray-800 flex flex-col"
          style={{
            borderRadius: 15,
            boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div className="flex flex-col gap-4 flex-shrink-0 p-5 pb-4" style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}>
            <div className="flex justify-between items-start">
              <div>
                <h1
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '16px',
                    fontWeight: 400,
                    color: '#06141B',
                  }}
                >
                  Meus convites
                </h1>
                <p
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 400,
                    color: '#9BA8AB',
                  }}
                >
                  {pendingCount > 0
                    ? `Você tem ${pendingCount} ${pendingCount === 1 ? 'convite pendente' : 'convites pendentes'}`
                    : 'Convites para workspaces'}
                </p>
              </div>
            </div>

            {/* Filtros - mesmo padrão das abas (Poppins 14px, #06141B ativo) */}
            <div className="flex gap-2 border-b border-gray-200" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {(['all', 'pending', 'accepted', 'rejected'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className="px-4 py-2 transition-colors border-b-2"
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    color: filter === f ? '#06141B' : '#9BA8AB',
                    borderBottomColor: filter === f ? '#06141B' : 'transparent',
                  }}
                >
                  {f === 'all' && 'Todos'}
                  {f === 'pending' && `Pendentes${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
                  {f === 'accepted' && 'Aceitos'}
                  {f === 'rejected' && 'Rejeitados'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 min-h-0 p-5 pt-4">
            {filteredInvites.length === 0 ? (
              <div
                className="text-center py-12"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#718096',
                  backgroundColor: '#F9FAFB',
                  borderRadius: 15,
                  border: '1px solid #E2E8F0',
                }}
              >
                Nenhum convite encontrado.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredInvites.map((invite) => {
                  return (
                    <div
                      key={invite.id}
                      className="rounded-[15px] p-4 transition-colors flex items-center gap-4"
                      style={{
                        backgroundColor: '#FBFBFB',
                        boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    >
                      <div className="flex-shrink-0 rounded-[10px] overflow-hidden bg-[#F5F6F5] flex items-center justify-center">
                        <WorkspaceThumbnail
                          imageUrl={invite.workspace?.imageUrl}
                          name={invite.workspace?.name}
                          size={40}
                          borderRadius={10}
                          letterClassName="text-[#06141B]"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#06141B', marginBottom: 4 }}>
                          {invite.workspace?.name || 'Workspace'}
                        </div>
                        {invite.role && (
                          <div style={{ fontSize: 12, color: '#718096', fontWeight: 400 }}>
                            Você foi convidado como {invite.role.name}
                          </div>
                        )}
                        {invite.status !== 'pending' && (
                          <div style={{ fontSize: 11, color: '#9BA8AB', marginTop: 4 }}>
                            {invite.status === 'accepted' && invite.acceptedAt && (
                              <>Aceito em {new Date(invite.acceptedAt).toLocaleDateString('pt-BR')}</>
                            )}
                            {invite.status === 'rejected' && (
                              <>Rejeitado em {new Date(invite.updatedAt).toLocaleDateString('pt-BR')}</>
                            )}
                            {invite.status === 'expired' && <>Expirado</>}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 flex-shrink-0">
                        {invite.status === 'pending' && (
                          <>
                            <button
                              onClick={() =>
                                acceptMutation.mutate({ token: invite.token, workspaceId: invite.workspaceId })
                              }
                              disabled={acceptMutation.isPending}
                              className="w-8 h-8 flex items-center justify-center rounded-[10px] transition-opacity hover:opacity-90 disabled:opacity-50"
                              style={{ backgroundColor: '#10B981', color: '#fff' }}
                              title="Aceitar convite"
                            >
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M13 4L6 11L3 8" />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm('Tem certeza que deseja rejeitar este convite?')) {
                                  rejectMutation.mutate(invite.token);
                                }
                              }}
                              disabled={rejectMutation.isPending}
                              className="w-8 h-8 flex items-center justify-center rounded-[10px] transition-opacity hover:opacity-90 disabled:opacity-50"
                              style={{ backgroundColor: '#06141B', color: '#F5F6F5' }}
                              title="Recusar convite"
                            >
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 4L12 12M12 4L4 12" />
                              </svg>
                            </button>
                          </>
                        )}
                        {invite.status === 'accepted' && (
                          <button
                            onClick={() => navigate({ to: `/w/${invite.workspaceId}/clients` })}
                            className="px-4 py-2 rounded-[10px] transition-opacity hover:opacity-90"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: 12,
                              fontWeight: 400,
                              backgroundColor: '#06141B',
                              color: '#F5F6F5',
                              boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                            }}
                          >
                            Acessar
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
